import {
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Paper,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DirectionsRunOutlinedIcon from '@mui/icons-material/DirectionsRunOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { athlete } from 'apis';
import { competitionTypeChipSx, getDisciplineChipSx } from '../../utils/chipStyles';

const sectionSx = {
    border: '1px solid #d9e3ec',
    borderRadius: 2,
    backgroundColor: '#fff',
    overflow: 'hidden'
} as const;

const resultChipSx = {
    height: 24,
    borderRadius: 1.5,
    fontWeight: 800,
    fontSize: '0.74rem',
    backgroundColor: '#eaf3fb',
    color: '#315b9f',
    '& .MuiChip-label': { px: 1 }
} as const;

const resultRowGridSx = {
    display: 'grid',
    gridTemplateColumns: {
        xs: 'minmax(74px, 0.65fr) minmax(120px, 1fr) repeat(5, minmax(78px, 0.8fr))',
        md: '120px minmax(160px, 1.2fr) repeat(5, minmax(100px, 0.75fr))'
    },
    alignItems: 'center',
    columnGap: 1.5,
    minWidth: 860
} as const;

const resultHeaderCellSx = {
    color: '#526173',
    fontSize: '0.72rem',
    fontWeight: 900,
    whiteSpace: 'nowrap'
} as const;

const resultBodyCellSx = {
    color: '#0f2338',
    fontSize: '0.88rem',
    fontWeight: 700,
    overflowWrap: 'anywhere'
} as const;

const MONTH_SHORT_LABELS = {
    el: ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαϊ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'],
    en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
} as const;

const getInitials = (name?: string) => {
    if (!name) return 'A';
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();
};

const valueOrDash = (value: any) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const toAccentlessUppercase = (value: string) =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase();

const getClubPath = (organizationId?: string | null) => (organizationId ? `/public/clubs/manage/${organizationId}/details` : '');

const linkValueSx = {
    m: 0,
    p: 0,
    border: 0,
    background: 'transparent',
    color: '#0b63b6',
    fontFamily: 'inherit',
    fontSize: '0.92rem',
    fontWeight: 900,
    lineHeight: 1.25,
    textAlign: 'left',
    textDecoration: 'none',
    cursor: 'pointer',
    overflowWrap: 'anywhere',
    '&:hover': {
        color: '#084f93',
        textDecoration: 'underline'
    }
} as const;

const ProfileFact = ({
    label,
    value,
    linkTo,
    tooltip
}: {
    label: string;
    value: any;
    linkTo?: string;
    tooltip?: string;
}) => {
    const displayValue = valueOrDash(value);
    const hasLink = Boolean(linkTo && displayValue !== '-');
    const renderedValue = hasLink ? (
        <Typography component={RouterLink} to={linkTo as string} sx={linkValueSx}>
            {displayValue}
        </Typography>
    ) : (
        <Typography sx={{ color: '#111827', fontSize: '0.92rem', fontWeight: 900, lineHeight: 1.25, overflowWrap: 'anywhere' }}>
            {displayValue}
        </Typography>
    );

    return (
        <Box
            sx={{
                minWidth: 0,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 0.75fr) minmax(0, 1fr)' },
                gap: { xs: 0.35, sm: 1.5 },
                alignItems: 'baseline',
                py: 1.05,
                borderBottom: '1px solid rgba(201, 221, 235, 0.72)'
            }}
        >
            <Typography sx={{ color: '#7a8794', fontSize: '0.78rem', fontWeight: 800, lineHeight: 1.15 }}>
                {toAccentlessUppercase(label)}
            </Typography>
            {tooltip && displayValue !== '-' ? (
                <Tooltip title={tooltip} arrow>
                    <Box component="span" sx={{ minWidth: 0, width: 'fit-content', maxWidth: '100%' }}>
                        {renderedValue}
                    </Box>
                </Tooltip>
            ) : (
                renderedValue
            )}
        </Box>
    );
};

const ProfileGroup = ({ title, icon, children }: { title: string; icon: any; children: any }) => (
    <Stack
        spacing={1.15}
        sx={{
            minWidth: 0,
            flex: 1,
            px: { xs: 0, md: 1.25 },
            py: { xs: 1, md: 0.5 }
        }}
    >
        <Stack direction="row" spacing={1.4} alignItems="center" sx={{ mb: 0.25 }}>
            <Box
                sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e7f3fc',
                    color: '#4b86b6'
                }}
            >
                {icon}
            </Box>
            <Typography sx={{ color: '#0f3b68', fontSize: { xs: '0.95rem', md: '1.05rem' }, fontWeight: 900, letterSpacing: 0 }}>
                {toAccentlessUppercase(title)}
            </Typography>
        </Stack>
        <Stack
            sx={{
                '& > *:last-child': {
                    borderBottom: 0
                }
            }}
        >
            {children}
        </Stack>
    </Stack>
);

const ResultChip = ({ label, title, sx = {} }: { label: any; title: any; sx?: any }) => {
    const chipLabel = valueOrDash(label);
    const tooltipTitle = valueOrDash(title);

    return (
        <Tooltip title={tooltipTitle === '-' ? chipLabel : tooltipTitle} arrow>
            <Chip size="small" label={chipLabel} sx={{ ...resultChipSx, ...sx }} />
        </Tooltip>
    );
};

const ResultMetric = ({ value, title, tone = 'default' }: { value: any; title?: any; tone?: 'default' | 'primary' | 'status' }) => {
    const label = valueOrDash(value);
    const tooltipTitle = valueOrDash(title);
    const isEmpty = label === '-';

    const metric = (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 44,
                minHeight: 26,
                px: 1,
                borderRadius: 1.5,
                color: isEmpty ? '#7a8794' : tone === 'primary' ? '#315b9f' : tone === 'status' ? '#0f766e' : '#1f2d3d',
                backgroundColor: isEmpty ? 'transparent' : tone === 'primary' ? '#edf5ff' : tone === 'status' ? '#e7f7f3' : '#f5f8fb',
                fontWeight: 900
            }}
        >
            {label}
        </Box>
    );

    if (tooltipTitle === '-') return metric;

    return (
        <Tooltip title={tooltipTitle} arrow>
            {metric}
        </Tooltip>
    );
};

const Details = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const language = (i18n.resolvedLanguage || i18n.language || 'el').toLowerCase();
    const languageKey = language.startsWith('el') ? 'el' : 'en';

    const profileQuery = useQuery({
        queryKey: ['public-athlete-profile', id],
        enabled: Boolean(id),
        queryFn: async () => athlete.getPublicAthleteProfile({ id })
    });

    const formatDate = (value?: string | null) => {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;

        const day = String(date.getDate()).padStart(2, '0');
        const month = MONTH_SHORT_LABELS[languageKey][date.getMonth()];
        return `${day} ${month} ${date.getFullYear()}`;
    };

    const formatDateParts = (value?: string | null) => {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;

        return {
            day: String(date.getDate()).padStart(2, '0'),
            month: MONTH_SHORT_LABELS[languageKey][date.getMonth()],
            year: String(date.getFullYear())
        };
    };

    const data = profileQuery.data;
    const profile = data?.athlete;
    const results = data?.results ?? [];
    const displayName = i18n.language?.startsWith('el') ? profile?.fullName : profile?.fullNameEn || profile?.fullName;
    const profileClubPath = profile?.organizationId && profile?.isOrganizationPublicVisible !== false ? getClubPath(profile.organizationId) : '';

    if (profileQuery.isLoading) {
        return (
            <Box sx={{ width: '100%', py: 6, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (profileQuery.isError) {
        return (
            <Stack spacing={2} sx={{ width: '100%', py: 3, alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 800 }}>{t('SomethingWrong')}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/public/athletes')}>
                    {t('BackToList')}
                </Button>
            </Stack>
        );
    }

    if (!profile) {
        return (
            <Stack spacing={2} sx={{ width: '100%', py: 3, alignItems: 'center' }}>
                <Typography sx={{ fontWeight: 800 }}>{t('AthleteNotFound')}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/public/athletes')}>
                    {t('BackToList')}
                </Button>
            </Stack>
        );
    }

    return (
        <Stack spacing={2.5} sx={{ width: '100%', minWidth: 0 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/public/athletes')}
                sx={{ alignSelf: 'flex-start', color: '#315b9f', fontWeight: 800 }}
            >
                {t('BackToList')}
            </Button>

            <Stack spacing={1}>
                <Paper
                    elevation={0}
                    sx={{
                        ...sectionSx,
                        p: 0,
                        width: '100%',
                        maxWidth: 1260,
                        backgroundColor: '#eef8ff',
                        boxShadow: '0 8px 24px rgba(15, 35, 56, 0.13)'
                    }}
                >
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        alignItems="stretch"
                        sx={{ minWidth: 0 }}
                    >
                        <Stack
                            direction={{ xs: 'row', md: 'column' }}
                            spacing={{ xs: 1.4, md: 1.6 }}
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                minWidth: { xs: 0, md: 275 },
                                flex: '0 0 auto',
                                p: { xs: 1.75, md: 2.25 },
                                backgroundColor: '#eaf6ff',
                                borderRight: { xs: 0, md: '1px solid #d4e4ef' },
                                borderBottom: { xs: '1px solid #c9ddeb', md: 0 }
                            }}
                        >
                            <Avatar
                                src={profile.avatar || undefined}
                                sx={{
                                    width: { xs: 74, md: 88 },
                                    height: { xs: 74, md: 88 },
                                    bgcolor: '#7eaaca',
                                    color: '#fff',
                                    fontWeight: 900,
                                    fontSize: '1.4rem',
                                    border: '4px solid #f5fbff',
                                    boxShadow: '0 6px 14px rgba(49, 91, 159, 0.18)'
                                }}
                            >
                                {getInitials(displayName)}
                            </Avatar>
                            <Box sx={{ minWidth: 0, textAlign: { xs: 'left', md: 'center' } }}>
                                <Typography sx={{ color: '#0f3b68', fontWeight: 900, fontSize: { xs: '1.05rem', md: '1.35rem' }, lineHeight: 1.18 }}>
                                    {displayName}
                                </Typography>
                                <Stack direction={{ xs: 'row', md: 'column' }} spacing={0.9} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mt: 2 }}>
                                    <Chip
                                        icon={<PersonOutlineOutlinedIcon />}
                                        label={`${t('RegNumber')} ${valueOrDash(profile.regNumber)}`}
                                        sx={{
                                            height: 32,
                                            borderRadius: 5,
                                            backgroundColor: '#f7fbff',
                                            border: '1px solid #b8d4ea',
                                            color: '#0f3b68',
                                            fontWeight: 900,
                                            '& .MuiChip-icon': { color: '#4b86b6' }
                                        }}
                                    />
                                    <Chip
                                        icon={<BadgeOutlinedIcon />}
                                        label={`${t('AthleteCode')} ${valueOrDash(profile.code1 || profile.code2)}`}
                                        sx={{
                                            height: 32,
                                            borderRadius: 5,
                                            backgroundColor: '#f7fbff',
                                            border: '1px solid #b8d4ea',
                                            color: '#0f3b68',
                                            fontWeight: 900,
                                            '& .MuiChip-icon': { color: '#4b86b6' }
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Stack>

                        <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            spacing={{ xs: 1.5, md: 1.5 }}
                            divider={
                                <Box
                                    sx={{
                                        width: { xs: '100%', lg: '1px' },
                                        height: { xs: '1px', lg: 'auto' },
                                        backgroundColor: '#d9e3ec'
                                    }}
                                />
                            }
                            sx={{ flex: 1, width: '100%', minWidth: 0, p: { xs: 1.5, md: 2 } }}
                        >
                            <ProfileGroup title={t('BasicInfo')} icon={<AssignmentOutlinedIcon />}>
                                <ProfileFact label={t('LastName')} value={profile.lastName} />
                                <ProfileFact label={t('FirstName')} value={profile.firstName} />
                                <ProfileFact label={t('YearOfBirth')} value={profile.yearOfBirth} />
                                <ProfileFact label={t('Nationality')} value={profile.nationality} />
                            </ProfileGroup>

                            <ProfileGroup title={t('AthleticInfo')} icon={<DirectionsRunOutlinedIcon />}>
                                <ProfileFact label={t('Gender')} value={profile.gender} />
                                <ProfileFact
                                    label={t('Clubs')}
                                    value={profile.organizationAbbr || profile.organization}
                                    linkTo={profileClubPath}
                                    tooltip={profile.organization}
                                />
                            </ProfileGroup>
                        </Stack>
                    </Stack>
                </Paper>
            </Stack>

            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.35}>
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#e7f3fc',
                            color: '#1976d2',
                            flex: '0 0 auto'
                        }}
                    >
                        <EmojiEventsOutlinedIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ color: '#0f3b68', fontWeight: 900, fontSize: { xs: '1.12rem', md: '1.35rem' }, lineHeight: 1.15 }}>
                        {t('Results')}
                    </Typography>
                    {profileQuery.isFetching && <CircularProgress size={20} />}
                </Stack>

                {results.length > 0 ? (
                    <Stack spacing={2} sx={{ position: 'relative', pl: { xs: 0, md: 6 } }}>
                        <Box
                            sx={{
                                display: { xs: 'none', md: 'block' },
                                position: 'absolute',
                                top: 15,
                                bottom: 18,
                                left: 23,
                                width: 2,
                                backgroundColor: '#b9d7ef'
                            }}
                        />
                        {results.map((competition: any) => {
                            const dateParts = formatDateParts(competition.startDate);

                            return (
                                <Stack
                                    key={competition.competitionId}
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={{ xs: 1, md: 2 }}
                                    alignItems={{ xs: 'stretch', md: 'stretch' }}
                                    sx={{ position: 'relative' }}
                                >
                                    <Box
                                        sx={{
                                            display: { xs: 'none', md: 'block' },
                                            position: 'absolute',
                                            left: -30,
                                            top: 60,
                                            width: 16,
                                            height: 16,
                                            borderRadius: '50%',
                                            backgroundColor: '#1976d2',
                                            border: '4px solid #eaf6ff',
                                            boxShadow: '0 0 0 1px #8bbce3'
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            flex: '0 0 auto',
                                            width: { xs: '100%', md: 120 },
                                            display: 'flex',
                                            alignItems: { xs: 'flex-start', md: 'center' },
                                            justifyContent: { xs: 'flex-start', md: 'center' }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: { xs: 120, md: 96 },
                                                minHeight: { xs: 46, md: 116 },
                                                px: 1,
                                                py: { xs: 0.8, md: 1.2 },
                                                borderRadius: 2,
                                                backgroundColor: '#f7fbff',
                                                border: '1px solid #c9ddeb',
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: { xs: 'row', md: 'column' },
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: { xs: 0.8, md: 0.25 },
                                                boxShadow: '0 4px 10px rgba(15, 35, 56, 0.08)'
                                            }}
                                        >
                                            <CalendarMonthOutlinedIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                                            {dateParts ? (
                                                <Stack spacing={0} alignItems="center">
                                                    <Typography sx={{ color: '#1976d2', fontWeight: 900, fontSize: { xs: '0.9rem', md: '1.8rem' }, lineHeight: 1 }}>
                                                        {dateParts.day}
                                                    </Typography>
                                                    <Typography sx={{ color: '#315b9f', fontWeight: 800, fontSize: { xs: '0.78rem', md: '0.92rem' }, lineHeight: 1.2 }}>
                                                        {dateParts.month}
                                                    </Typography>
                                                    <Typography sx={{ color: '#6b7787', fontWeight: 800, fontSize: { xs: '0.74rem', md: '0.86rem' }, lineHeight: 1.3 }}>
                                                        {dateParts.year}
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Typography sx={{ color: '#315b9f', fontWeight: 900 }}>{formatDate(competition.startDate) || '-'}</Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    <Paper
                                        elevation={0}
                                        sx={{
                                            ...sectionSx,
                                            flex: 1,
                                            borderColor: '#d5e3ee',
                                            boxShadow: '0 4px 12px rgba(15, 35, 56, 0.1)'
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1.2}
                                            alignItems="center"
                                            sx={{
                                                px: { xs: 1.4, md: 2 },
                                                py: 1.55,
                                                backgroundColor: '#f7fbff',
                                                borderBottom: '1px solid #d9e3ec',
                                                minWidth: 0
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: '50%',
                                                    display: { xs: 'none', sm: 'flex' },
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    backgroundColor: '#e7f3fc',
                                                    color: '#1976d2',
                                                    flex: '0 0 auto'
                                                }}
                                            >
                                                <EmojiEventsOutlinedIcon sx={{ fontSize: 20 }} />
                                            </Box>
                                            <Typography
                                                component="button"
                                                type="button"
                                                onClick={() => navigate(`/public/competition?id=${competition.competitionId}`)}
                                                sx={{
                                                    color: '#0f2338',
                                                    fontWeight: 900,
                                                    fontSize: { xs: '0.95rem', md: '1.08rem' },
                                                    lineHeight: 1.25,
                                                    overflowWrap: 'anywhere',
                                                    p: 0,
                                                    border: 0,
                                                    background: 'transparent',
                                                    textAlign: 'left',
                                                    cursor: 'pointer',
                                                    fontFamily: 'inherit',
                                                    minWidth: 0,
                                                    '&:hover': {
                                                        color: '#006dba',
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                {i18n.language?.startsWith('el')
                                                    ? competition.competitionName
                                                    : competition.competitionNameEn || competition.competitionName}
                                            </Typography>
                                            <ResultChip
                                                label={competition.competitionTypeAbbr || competition.competitionType}
                                                title={competition.competitionType}
                                                sx={competitionTypeChipSx}
                                            />
                                        </Stack>

                                        <Box sx={{ overflowX: 'auto' }}>
                                            <Box sx={{ px: { xs: 1.2, md: 2 }, py: 1.05, backgroundColor: '#eef6fb', borderBottom: '1px solid #d4e4ef' }}>
                                                <Box sx={resultRowGridSx}>
                                                    <Typography sx={resultHeaderCellSx}>{t('Discipline')}</Typography>
                                                    <Typography sx={resultHeaderCellSx}>{t('Round')}</Typography>
                                                    <Typography sx={{ ...resultHeaderCellSx, textAlign: 'center' }}>{t('DisplayResult')}</Typography>
                                                    <Typography sx={{ ...resultHeaderCellSx, textAlign: 'center' }}>{t('Status')}</Typography>
                                                    <Typography sx={{ ...resultHeaderCellSx, textAlign: 'center' }}>{t('Rank')}</Typography>
                                                    <Typography sx={{ ...resultHeaderCellSx, textAlign: 'center' }}>{t('Points')}</Typography>
                                                    <Typography sx={{ ...resultHeaderCellSx, textAlign: 'center' }}>{t('Bib')}</Typography>
                                                </Box>
                                            </Box>

                                            <Stack>
                                                {competition.rows.map((row: any, index: number) => (
                                                    <Box
                                                        key={row.resultId ?? `${row.participationId}-${index}`}
                                                        sx={{
                                                            px: { xs: 1.2, md: 2 },
                                                            py: 1.1,
                                                            borderBottom: index === competition.rows.length - 1 ? 0 : '1px solid #e1e8ef',
                                                            '&:nth-of-type(even)': { backgroundColor: '#fafcfe' },
                                                            '&:hover': { backgroundColor: '#edf5ff' }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={resultRowGridSx}
                                                        >
                                                            <Box sx={resultBodyCellSx}>
                                                                <ResultChip
                                                                    label={
                                                                        row.disciplineAbbr ||
                                                                        row.discipline ||
                                                                        competition.disciplineAbbr ||
                                                                        competition.discipline
                                                                    }
                                                                    title={row.discipline || competition.discipline}
                                                                    sx={getDisciplineChipSx(
                                                                        row.disciplineAbbr ||
                                                                            row.discipline ||
                                                                            competition.disciplineAbbr ||
                                                                            competition.discipline
                                                                    )}
                                                                />
                                                            </Box>
                                                            <Typography sx={resultBodyCellSx}>{valueOrDash(row.round || row.roundAbbr)}</Typography>
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <ResultMetric value={row.displayResult} tone="primary" />
                                                            </Box>
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <ResultMetric value={row.statusAbbr || row.status} title={row.status} tone="status" />
                                                            </Box>
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <ResultMetric value={row.rank ?? row.position} />
                                                            </Box>
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <ResultMetric value={row.points} />
                                                            </Box>
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <ResultMetric value={row.bib} />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    </Paper>
                                </Stack>
                            );
                        })}
                    </Stack>
                ) : (
                    <Paper elevation={0} sx={{ ...sectionSx, p: 2, textAlign: 'center' }}>
                        <Typography sx={{ color: theme.palette.text.secondary }}>{t('NoData')}</Typography>
                    </Paper>
                )}
            </Stack>
        </Stack>
    );
};

export default Details;
